import { useMutation, useQuery } from "@apollo/client";
import { Autocomplete, Button, Stack, TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useState } from "react";

import type { FlagRow } from "./FlagsTab";

import { gql } from "../../../generated-graphql";

/**
 * Pulls every distinct annotation tag in use across all flags. The
 * result drives the autocomplete on the tag input — the field is
 * `freeSolo`, so staff can also type a brand-new tag and it gets added
 * to the next render of this query (after refetch).
 */
const GET_ALL_FEATURE_FLAG_TAGS = gql(`
  query GetAllFeatureFlagTags {
    getAllFeatureFlagTags
  }
`);

/**
 * Upsert the annotation row for a single flag. The mutation returns the
 * updated `FeatureFlag`, which Apollo's cache merges into the
 * `StaffFlagsList` query result by `key` so the grid + drawer reflect
 * the change immediately.
 */
const UPSERT_FEATURE_FLAG_ANNOTATION = gql(`
  mutation UpsertFeatureFlagAnnotation($input: UpsertFeatureFlagAnnotationInput!) {
    upsertFeatureFlagAnnotation(input: $input) {
      key
      notes
      tags
      annotationUpdatedAt
      annotationUpdatedBy
    }
  }
`);

/**
 * Inline editor for a flag's staff-editable annotation: free-text
 * `notes` and a multi-select `tags` array. Lives inside
 * `FlagDetailDrawer` and toggles into view when staff click "Edit notes
 * & tags".
 */
export function FlagAnnotationEditor({
  flag,
  onCancel,
  onSaved,
}: {
  flag: FlagRow;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const alerts = useAlerts();
  const [notes, setNotes] = useState<string>(flag.notes ?? "");
  const [tags, setTags] = useState<string[]>([...flag.tags]);

  const { data: tagsData } = useQuery(GET_ALL_FEATURE_FLAG_TAGS, {
    fetchPolicy: "cache-and-network",
  });
  const tagOptions = tagsData?.getAllFeatureFlagTags ?? [];

  const [save, { loading: saving }] = useMutation(UPSERT_FEATURE_FLAG_ANNOTATION, {
    refetchQueries: ["StaffFlagsList", "GetAllFeatureFlagTags"],
  });

  const dirty = notes !== (flag.notes ?? "") || !sameTags(tags, flag.tags);

  const handleSave = async () => {
    try {
      await save({
        variables: {
          input: {
            key: flag.key,
            notes: notes.trim().length === 0 ? null : notes,
            // Trim + dedupe so a stray whitespace tag from free-solo
            // input doesn't sneak through.
            tags: Array.from(new Set(tags.map((t) => t.trim()).filter((t) => t.length > 0))),
          },
        },
      });
      alerts.success(`Saved annotation for ${flag.label}`);
      onSaved();
    } catch (err) {
      alerts.error("Failed to save annotation", err);
    }
  };

  return (
    <Stack spacing={2}>
      {/* Inner Stack with extra spacing dedicated to the two distinct
          input fields so they don't crowd each other. The outer Stack
          keeps its tighter spacing for the gap between this group and
          the action buttons row. */}
      <Stack spacing={3}>
        <TextField
          label="Notes"
          multiline
          minRows={3}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Internal notes for staff."
        />
        <Autocomplete
          multiple
          freeSolo
          options={tagOptions}
          value={tags}
          onChange={(_e, newTags) => {
            // Strip empty / whitespace-only entries on the way in so the
            // chip list never renders a bare `""` chip.
            setTags(newTags.map((t) => t.trim()).filter((t) => t.length > 0));
          }}
          renderInput={(params) => <TextField {...params} label="Tags" placeholder="Add tag…" />}
          size="small"
        />
      </Stack>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!dirty || saving} onClick={handleSave}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </Stack>
    </Stack>
  );
}

function sameTags(a: string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((tag, idx) => tag === sortedB[idx]);
}
